import H3 from "../vectors";
import { getDistanceParams } from "../utils";
import { getObjFromArrByKeyValuePair } from "../../utils";
import { clamp } from "../utils";

function hyp2f1b(x: number): number {
  let res = 1.0;
  let resOld = 1.0;
  let term = 1.0;
  let ii = 0;
  while (true) {
    term = (((term * (3 + ii) * (1 + ii)) / (5 / 2 + ii)) * x) / (ii + 1);
    resOld = res;
    res += term;
    if (resOld == res) {
      return res;
    }
    ii += 1;
  }
}

function initial_guess(T: number, ll: number, M: number): Array<number> {
  if (M == 0) {
    const T0 = Math.acos(ll) + ll * Math.sqrt(1 - ll * ll) + M * Math.PI;
    const T1 = (2 * (1 - ll * ll * ll)) / 3;
    let x0 = 0;
    if (T >= T0) {
      x0 = Math.pow(T0 / T, 2 / 3) - 1;
    } else if (T < T1) {
      x0 = ((((5 / 2) * T1) / T) * (T1 - T)) / (1 - Math.pow(ll, 5)) + 1;
    } else {
      x0 = Math.pow(T0 / T, Math.log2(T1 / T0)) - 1;
    }
    return [x0];
  } else {
    const pi = Math.PI;
    const x_0l =
      (((M * pi + pi) / (8 * T)) ** (2 / 3) - 1) /
      (((M * pi + pi) / (8 * T)) ** (2 / 3) + 1);
    const x_0r =
      (((8 * T) / (M * pi)) ** (2 / 3) - 1) /
      (((8 * T) / (M * pi)) ** (2 / 3) + 1);

    return [x_0l, x_0r];
  }
}

function tofEq(x: number, T0: number, ll: number, M: number): number {
  return tofEqY(x, computeY(x, ll), T0, ll, M);
}

function computeTmin(
  ll: number,
  M: number,
  numiter: number,
  rtol: number
): number {
  if (ll == 1) {
    return tofEq(0.0, 0.0, ll, M);
  } else {
    if (M == 0) {
      return 0.0;
    } else {
      const xi = 0.1;
      const Ti = tofEq(xi, 0.0, ll, M);
      const xTmin = halley(xi, Ti, ll, rtol, numiter);
      const Tmin = tofEq(xTmin as number, 0.0, ll, M);
      return Tmin;
    }
  }
}

function computeY(x: number, ll: number): number {
  return Math.sqrt(1 - ll ** 2 * (1 - x ** 2));
}

function computePsi(x: number, y: number, ll: number): number {
  if (-1 <= x && x < 1) {
    return Math.acos(x * y + ll * (1 - x * x));
  } else if (1 < x) {
    return Math.asinh((y - x * ll) * Math.sqrt(x * x - 1));
  } else {
    return 0.0;
  }
}

function tofEqY(x: number, y: number, T0: number, ll: number, M: number) {
  let T = 0;
  if (M == 0 && Math.sqrt(0.6) < x && x < Math.sqrt(1.4)) {
    const eta = y - ll * x;
    const S1 = (1 - ll - x * eta) * 0.5;
    const Q = (4 / 3) * hyp2f1b(S1);
    T = (eta * eta * eta * Q + 4 * ll * eta) * 0.5;
  } else {
    const psi = computePsi(x, y, ll);
    T =
      ((psi + M * Math.PI) / Math.sqrt(Math.abs(1 - x * x)) - x + ll * y) /
      (1 - x * x);
  }
  return T - T0;
}

function tofEqP(x: number, y: number, T: number, ll: number): number {
  return (3 * T * x - 2 + (2 * ll * ll * ll * x) / y) / (1 - x * x);
}

function tofEqP2(
  x: number,
  y: number,
  T: number,
  dT: number,
  ll: number
): number {
  return (
    (3 * T + 5 * x * dT + (2 * (1 - ll * ll) * ll * ll * ll) / (y * y * y)) /
    (1 - x * x)
  );
}

function tofEqP3(
  x: number,
  y: number,
  dT: number,
  ddT: number,
  ll: number
): number {
  return (
    (7 * x * ddT + 8 * dT - (6 * (1 - ll * ll) * ll ** 5 * x) / y ** 5) /
    (1 - x ** 2)
  );
}

function halley(
  p0: number,
  T0: number,
  ll: number,
  tol: number,
  maxiter: number
): number | void {
  let y = 0;
  let p = 0;
  let fder = 0;
  let fder2 = 0;
  let fder3 = 0;
  for (let ii = 0; ii < maxiter; ii++) {
    y = computeY(p0, ll);
    fder = tofEqP(p0, y, T0, ll);
    fder2 = tofEqP2(p0, y, T0, fder, ll);
    if (fder2 == 0) {
      console.log("fder2 was 0");
    }
    fder3 = tofEqP3(p0, y, fder, fder2, ll);
    p = p0 - (2 * fder * fder2) / (2 * fder2 ** 2 - fder * fder3);
    if (Math.abs(p - p0) < tol) {
      return p;
    }
    p0 = p;
  }
  console.log("halley failed :(");
}

function householder(
  p0: number,
  T0: number,
  ll: number,
  M: number,
  tol: number,
  maxiter: number
): number | void {
  let y = 0;
  let p = 0;
  let fval = 0;
  let T = 0;
  let fder = 0;
  let fder2 = 0;
  let fder3 = 0;
  for (let i = 0; i < maxiter; i++) {
    y = computeY(p0, ll);
    fval = tofEqY(p0, y, T0, ll, M);
    T = fval + T0;
    fder = tofEqP(p0, y, T, ll);
    fder2 = tofEqP2(p0, y, T, fder, ll);
    fder3 = tofEqP3(p0, y, fder, fder2, ll);
    p =
      p0 -
      fval *
        ((fder * fder - (fval * fder2) / 2) /
          (fder * (fder * fder - fval * fder2) + (fder3 * fval * fval) / 6));
    if (Math.abs(p - p0) < tol) {
      return p;
    }
    p0 = p;
  }
  console.log("householder failed");
}

export function findXY(
  ll: number,
  T: number,
  M: number,
  numiter: number,
  rtol: number
): Array<{ x: number; y: number }> {
  let Mmax = Math.floor(T / Math.PI);
  const T00 = Math.acos(ll) + ll * Math.sqrt(1 - ll * ll);

  // Refine maximum number of revolutions if necessary (NotImplemented)
  if (T < T00 + Mmax * Math.PI && Mmax > 0) {
    const Tmin = computeTmin(ll, Mmax, numiter, rtol);
    if (T < Tmin) {
      Mmax -= 1;
    }
  }
  if (M > Mmax) {
    console.log("M > Mmax error");
  }

  const initGuesses = initial_guess(T, ll, M);
  let xys: Array<{ x: number; y: number }> = [];
  initGuesses.forEach(x0 => {
    const x = <number>householder(x0, T, ll, M, rtol, numiter);
    const y = computeY(x as number, ll);
    xys.push({ x: x, y: y });
  });
  return xys;
}

export function reconstruct(
  x: number,
  y: number,
  r1: number,
  r2: number,
  ll: number,
  gamma: number,
  rho: number,
  sigma: number
): { Vr1: number; Vr2: number; Vt1: number; Vt2: number } {
  const Vr1 = (gamma * (ll * y - x - rho * (ll * y + x))) / r1;
  const Vr2 = (-gamma * (ll * y - x + rho * (ll * y + x))) / r2;
  const Vt1 = (gamma * sigma * (y + ll * x)) / r1;
  const Vt2 = (gamma * sigma * (y + ll * x)) / r2;
  return { Vr1, Vr2, Vt1, Vt2 };
}

export function planFlight(
  tof: number,
  r1: Vector,
  r2: Vector,
  mu: number,
  M = 0,
  rtol = 1e-8,
  maxiter = 35
): Array<{ initVel: Vector; finalVel: Vector }> {
  const v1 = new H3();
  const r1Rel = v1.set(r1).toObject();
  const r2Rel = v1.set(r2).toObject();

  const c = v1
    .set(r2Rel)
    .subtract(r1Rel)
    .toObject();
  const cNorm = v1.set(c).getLength();
  const r1Norm = v1.set(r1Rel).getLength();
  const r2Norm = v1.set(r2Rel).getLength();
  const s = 0.5 * (r1Norm + r2Norm + cNorm);

  const ir1 = v1
    .set(r1)
    .divideByScalar(r1Norm)
    .toObject();
  const ir2 = v1
    .set(r2)
    .divideByScalar(r2Norm)
    .toObject();
  let ih = v1
    .set(ir1)
    .cross(ir2)
    .normalise()
    .toObject();

  let ll = Math.sqrt(1 - Math.min(1.0, cNorm / s));

  if (ih.z < 0) {
    ll = -ll;
    ih = v1
      .set(ih)
      .multiplyByScalar(-1)
      .toObject();
  }

  const it1 = v1
    .set(ih)
    .cross(ir1)
    .toObject();
  const it2 = v1
    .set(ih)
    .cross(ir2)
    .toObject();

  const T = Math.sqrt((2 * mu) / s ** 3) * tof;

  const xy = findXY(ll, T, M, maxiter, rtol);

  const gamma = Math.sqrt((mu * s) / 2);
  const rho = (r1Norm - r2Norm) / cNorm;
  const sigma = Math.sqrt(1 - rho ** 2);

  let result: Array<{ initVel: Vector; finalVel: Vector }> = [];
  xy.forEach(i => {
    const { Vr1, Vr2, Vt1, Vt2 } = reconstruct(
      i.x,
      i.y,
      r1Norm,
      r2Norm,
      ll,
      gamma,
      rho,
      sigma
    );
    const initVel = v1
      .set(ir1)
      .multiplyByScalar(Vr1)
      .addScaledVector(Vt1, it1)
      .toObject();
    const finalVel = v1
      .set(ir2)
      .multiplyByScalar(Vr2)
      .addScaledVector(Vt2, it2)
      .toObject();
    result.push({ initVel: initVel, finalVel: finalVel });
  });

  return result;
}

export function stateToKepler(
  r: { x: number; y: number; z: number },
  v: { x: number; y: number; z: number },
  mu: number
): OrbitalElements {
  let v1 = new H3();
  let v2 = new H3();
  const kHat = { x: 0, y: 0, z: 1 };
  const rNorm = v1.set(r).getLength();
  const vNorm = v1.set(v).getLength();

  const h = v1
    .set(r)
    .cross(v2.set(v))
    .toObject(); // v1.set(r).cross(v).toObject();
  const hNorm = v1.set(h).getLength();
  const n = v1
    .set(kHat)
    .cross(v2.set(h))
    .toObject(); // normalise?
  const nNorm = v1.set(n).getLength();
  const e1 = v1
    .set(r)
    .multiplyByScalar(vNorm ** 2 - mu / rNorm)
    .toObject();
  const e2 = v1.set(r).dot(v2.set(v));
  const eVec = v1
    .set(e1)
    .subtractScaledVector(e2, v2.set(v))
    .divideByScalar(mu)
    .toObject();
  const e = v1.set(eVec).getLength();
  const E = vNorm ** 2 / 2 - mu / rNorm;
  let a = 0;
  if (e != 1) {
    a = -mu / (2 * E);
  } else {
    a = Infinity;
  }

  const neDot = v1.set(eVec).dot(v2.set(n));
  const reDot = v1.set(r).dot(v2.set(eVec));
  const rvDot = v1.set(r).dot(v2.set(v));

  const i = Math.acos(h.z / hNorm);
  if (i == 0) {
    var lAn = 0;
  } else {
    var lAn = Math.acos(n.x / nNorm);
  }
  if (n.y < 0) {
    lAn = 2 * Math.PI - lAn;
  }
  if (i == 0) {
    var argP = Math.atan2(eVec.y, eVec.x);
    if (h.z < 0) {
      argP = 2 * Math.PI - argP;
    }
  } else {
    var argP = Math.acos(neDot / (nNorm * e));
    if (eVec.z < 0) {
      argP = 2 * Math.PI - argP;
    }
  }

  let trueAnom = Math.acos(reDot / (e * rNorm));

  if (rvDot < 0) {
    trueAnom = 2 * Math.PI - trueAnom;
  }

  let eccAnom = 0;
  let meanAnom = 0;
  if (e > 1) {
    eccAnom =
      2 * Math.atanh(Math.sqrt((e - 1) / (e + 1)) * Math.tan(trueAnom / 2));
    meanAnom = e * Math.sinh(eccAnom) - eccAnom;
  } else if (e < 1) {
    eccAnom =
      2 * Math.atan2(Math.tan(trueAnom / 2), Math.sqrt((1 + e) / (1 - e)));
    meanAnom = eccAnom - e * Math.sin(eccAnom);
  }

  //const convFactor = 180 / Math.PI;

  return {
    a: a, // semi-major axis
    e: e, // eccentricity
    i: i, // inclination
    argP: argP, // argument of periapsis
    lAn: lAn, // longitude of ascending node
    trueAnom: trueAnom, // true anomaly
    eccAnom: eccAnom, // eccentric anomly
    meanAnom: meanAnom // mean anomaly
  };
}

/*

export function stateToKepler2(
  r: { x: number; y: number; z: number },
  v: { x: number; y: number; z: number },
  mu: number
): OrbitalElements {
  let v1 = new H3();
  let v2 = new H3();
  const rNorm = v1.set(r).getLength();
  const vNorm = v1.set(v).getLength();
  const hVec = v1
    .set(r)
    .cross(v2.set(v))
    .toObject();
  const eVec1 = v1
    .set(v)
    .cross(v2.set(hVec))
    .divideByScalar(mu)
    .toObject();
  const rHat = v1
    .set(r)
    .divideByScalar(rNorm)
    .toObject();
  const eVec = v1
    .set(eVec1)
    .subtract(v2.set(rHat))
    .toObject();
  const e = v1.set(eVec).getLength();

  return {
    a: 1, // semi-major axis
    e: 0, // eccentricity
    i: 1, // inclination
    argP: 1, // argument of periapsis
    lAn: 1, // longitude of ascending node
    trueAnom: 1, // true anomaly
    eccAnom: 1, // eccentric anomly
    meanAnom: 1 // mean anomaly
  };
}

*/

export function keplerToState(
  orb: OrbitalElements,
  gm: number
): { posRel: Vector; velRel: Vector } {
  // returns the position and velocity of the orbiting body RELATIVE to the primary body.
  const a = orb.a;
  const e = orb.e;
  const i = orb.i;
  const argP = orb.argP;
  const lAn = orb.lAn;
  const eccAnom = orb.eccAnom;

  let o = { x: 0, y: 0, z: 0 };
  let o_dot = { x: 0, y: 0, z: 0 };
  if (e < 1) {
    const r_c = a * (1 - e * Math.cos(eccAnom)); // distance to central body
    //vectors in orbital plane
    //o = {x: r_c * Math.cos(trueAnom), y: r_c * Math.sin(trueAnom), z: 0}; // position in orbital plane
    const o_dot_factor = Math.sqrt(gm * a) / r_c;
    o.x = a * (Math.cos(eccAnom) - e);
    o.y = a * Math.sin(eccAnom) * Math.sqrt(1 - Math.pow(e, 2));
    o_dot = {
      x: -Math.sin(eccAnom) * o_dot_factor,
      y: Math.sqrt(1 - e * e) * Math.cos(eccAnom) * o_dot_factor,
      z: 0
    }; // velocity in orbital plane
  } else if (e > 1) {
    o = {
      x: -a * (e - Math.cosh(eccAnom)),
      y: -a * Math.sqrt(e * e - 1) * Math.sinh(eccAnom),
      z: 0
    };
    const mean_motion = Math.sqrt(gm / Math.pow(Math.abs(a), 3));
    const o_dot_factor = (a * mean_motion) / (e * Math.cosh(eccAnom) - 1);
    o_dot.x = o_dot_factor * Math.sinh(eccAnom);
    o_dot.y = -o_dot_factor * Math.sqrt(e * e - 1) * Math.cosh(eccAnom);
  }
  const X =
    o.x *
      (Math.cos(argP) * Math.cos(lAn) -
        Math.sin(argP) * Math.cos(i) * Math.sin(lAn)) -
    o.y *
      (Math.sin(argP) * Math.cos(lAn) +
        Math.cos(argP) * Math.cos(i) * Math.sin(lAn)); // x-coordinate of position in inertial reference frame
  const Y =
    o.x *
      (Math.cos(argP) * Math.sin(lAn) +
        Math.sin(argP) * Math.cos(i) * Math.cos(lAn)) +
    o.y *
      (Math.cos(argP) * Math.cos(i) * Math.cos(lAn) -
        Math.sin(argP) * Math.sin(lAn));
  const Z =
    o.x * (Math.sin(argP) * Math.sin(i)) + o.y * (Math.cos(argP) * Math.sin(i));
  const VX =
    o_dot.x *
      (Math.cos(argP) * Math.cos(lAn) -
        Math.sin(argP) * Math.cos(i) * Math.sin(lAn)) -
    o_dot.y *
      (Math.sin(argP) * Math.cos(lAn) +
        Math.cos(argP) * Math.cos(i) * Math.sin(lAn));
  const VY =
    o_dot.x *
      (Math.cos(argP) * Math.sin(lAn) +
        Math.sin(argP) * Math.cos(i) * Math.cos(lAn)) +
    o_dot.y *
      (Math.cos(argP) * Math.cos(i) * Math.cos(lAn) -
        Math.sin(argP) * Math.sin(lAn));
  const VZ =
    o_dot.x * (Math.sin(argP) * Math.sin(i)) +
    o_dot.y * (Math.cos(argP) * Math.sin(i));
  return { posRel: { x: X, y: Y, z: Z }, velRel: { x: VX, y: VY, z: VZ } };
}

function solveKeplerEq(meanAnom: number, e: number, tol = 1e-14): number {
  // Solves Keplers third law M = E - e*sin(E) for E using Newton-Raphson
  let E = meanAnom;
  let E_last = E + 1e3 * tol; // make sure we enter the while loop
  if (e < 1) {
    while (Math.abs(E - E_last) > tol) {
      E_last = E;
      E = E - (E - e * Math.sin(E) - meanAnom) / (1 - e * Math.cos(E));
    }
  } else if (e > 1) {
    let s = Math.sign(meanAnom);
    let E0 = 1e-3 * s;
    while (Math.sign(meanAnom - e * Math.sinh(E0) + E0) == s) {
      // Find a sign change and use it as inital value
      E0 += s;
    }
    E = E0;
    E_last = E + 1e3 * tol; // make sure we enter the while loop
    while (Math.abs(E - E_last) > tol) {
      E_last = E;
      E = E + (meanAnom - e * Math.sinh(E) + E) / (e * Math.cosh(E) - 1);
    }
  }
  return E;
}

export function propagateOrbitalElements(
  orb: OrbitalElements,
  dt: number,
  gm: number
): OrbitalElements {
  const mean_motion = Math.sqrt(gm / Math.pow(Math.abs(orb.a), 3));
  let meanAnomNew = 0;
  if (orb.e < 1) {
    meanAnomNew = (orb.meanAnom + dt * mean_motion) % (2 * Math.PI); // calculate new mean anomaly and normalize it to [0, 2pi)
  } else if (orb.e > 1) {
    meanAnomNew = orb.meanAnom + dt * mean_motion;
  }
  const eccAnomNew = solveKeplerEq(meanAnomNew, orb.e);

  let trueAnomNew = 0;
  if (orb.e < 1) {
    trueAnomNew =
      2 *
      Math.atan2(
        Math.sqrt(1 + orb.e) * Math.sin(eccAnomNew / 2),
        Math.sqrt(1 - orb.e) * Math.cos(eccAnomNew / 2)
      );
  } else if (orb.e > 1) {
    trueAnomNew =
      2 *
      Math.atan2(
        Math.sqrt(1 + orb.e) * Math.tanh(eccAnomNew / 2),
        Math.sqrt(orb.e - 1)
      );
  }
  return {
    a: orb.a, // semi-major axis
    e: orb.e, // eccentricity
    i: orb.i, // inclination
    argP: orb.argP, // argument of periapsis
    lAn: orb.lAn, // longitude of ascending node
    trueAnom: trueAnomNew, // true anomaly
    eccAnom: eccAnomNew, // eccentric anomly
    meanAnom: meanAnomNew // mean anomaly
  };
}

export function radiusSOI(largerMass: SOITree, smallerMass: SOITree): number {
  const d = Math.sqrt(getDistanceParams(largerMass, smallerMass).dSquared);
  return d * Math.pow(smallerMass.m / largerMass.m, 2 / 5);
}

function recursiveTree(tree: SOITree): SOITree {
  if (tree.children.length == 0) {
    return tree;
  }
  let children = tree.children;
  // Calculate the Sphere of Influence relative to the tree root
  for (let i = 0; i < tree.children.length; i++) {
    children[i].SOIradius = radiusSOI(tree, children[i]);
  }
  // Sort and place bodies
  var len = children.length;
  var i = 0;
  while (i < len) {
    var j = 0;
    while (j < len) {
      if (i != j) {
        const d = Math.sqrt(
          getDistanceParams(children[i], children[j]).dSquared
        );
        if (d < children[i].SOIradius) {
          children[i].children.push(children[j]);
          children.splice(j, 1);
          len -= 1;
          if (j < i) {
            i--;
          }
        } else {
          j++;
        }
      } else {
        j++;
      }
    }
    i = i + 1;
  }
  // Do all of the above recursivly for all remaining direct children of the root
  for (let i = 0; i < children.length; i++) {
    children[i] = recursiveTree(children[i]);
  }
  tree.children = children;
  return tree;
}

// remove position as it may change
function simplifyTree(tree: SOITree): SOITree {
  let newTree = {
    name: tree.name,
    children: tree.children,
    SOIradius: tree.SOIradius
  };
  if (newTree.children.length == 0) {
    return newTree;
  }
  for (let i = 0; i < newTree.children.length; i++) {
    newTree.children[i] = simplifyTree(newTree.children[i]);
  }
  return newTree;
}

// masses is scenario.masses
export function constructSOITree(masses: Array<MassType>): SOITree {
  //const sun: MassType = getObjFromArrByKeyValuePair(masses, 'name', 'Sun');
  let sun = masses[0];
  // Search for the first star, otherwise use the first mass as root
  for (let i = 0; i < masses.length; i++) {
    if (masses[i].massType == "star") {
      sun = masses[i];
      break;
    }
  }
  let tree: SOITree = {
    SOIradius: 1e100,
    children: [],
    name: sun?.name,
    m: sun?.m,
    x: sun?.x,
    y: sun?.y,
    z: sun?.z
  };
  masses.forEach(val => {
    if (val.name != sun.name) {
      let newVal: SOITree = {
        SOIradius: 0,
        children: [],
        name: val.name,
        m: val.m,
        x: val.x,
        y: val.y,
        z: val.z
      };
      tree.children.push(newVal);
    }
  });
  tree = recursiveTree(tree);
  // construct simplified tree with just name and SOIradius
  const simpleTree = simplifyTree(tree);
  return simpleTree;
}

// pos is the position and masses is scenario.masses
export function findCurrentSOI(
  pos: MassType,
  tree: SOITree,
  masses: Array<MassType>
): MassType {
  if (tree.children.length == 0) {
    return getObjFromArrByKeyValuePair(masses, "name", tree.name);
  }
  for (let i = 0; i < tree.children.length; i++) {
    if (tree.children[i].name == pos.name) {
      continue;
    }
    const currentBody: MassType = getObjFromArrByKeyValuePair(
      masses,
      "name",
      tree.children[i].name
    );
    const d = Math.sqrt(getDistanceParams(currentBody, pos).dSquared);
    if (d < tree.children[i].SOIradius) {
      return findCurrentSOI(pos, tree.children[i], masses);
    }
  }
  return {
    ...getObjFromArrByKeyValuePair(masses, "name", tree.name),
    soi: tree.SOIradius
  };
}

export function reverseAcceleration(
  pos: MassType,
  primary: MassType,
  g = 39.5
): Vector {
  const rVec = new H3().set({
    x: pos.x - primary.x,
    y: pos.y - primary.y,
    z: pos.z - primary.z
  });
  const r = rVec.getLength();
  const reverseAcc = rVec.multiplyByScalar((g * primary.m) / r ** 3).toObject();
  return reverseAcc;
}
/*
export function allPassingSOI(tree: SOITree, name: string){
  if (tree.name == name){
    return name;
  }
  // Check tree's children
  for (let i = 0; i < tree.children.length; i++){
    if (tree.children[i].name == name){
      return name;
    }
  }
  // Check tree's grandchildren
  for (let i = 0; i < tree.children.length; i++){
  }
}
*/

// ellipseTest() {

//   /*
//   const rEarth = {x: 0.4240363252016235, y: -0.9248449798862485, z: -1.232690294681233e-4};
//   const rMars = {x: 0.1609433119907515, y: -1.432665781988499, z: -0.03393579925893126};
//   const g = 39.5;
//   const Sun = {m: 1, x: 0.004494747940528018,
//     y: 9.145777867796766e-4,
//     z: -6.127893755128986e-5,
//     vx: -1.7443876658803292e-4,
//     vy: 0.002043973630637931,
//     vz: -4.697196039923407e-6
//   };

//   const tof = 0.5;
//   const maxiter = 35;
//   const M = 0;
//   const rtol = 1e-8;
//   const vs = planFlight(tof, rEarth, rMars, Sun, g, M, rtol, maxiter);
//   console.log(vs);

//   const scenario = this.scenario;
//   const primary = getObjFromArrByKeyValuePair(
//     scenario.masses,
//     'name',
//     scenario.primary
//   );
//   const scale = scenario.scale;
//   const ellipse = getEllipse(0.7776923423682557, 0.8667555109899555);
//   this.ellipseCurve.position.z =
//     (this.rotatingReferenceFrame.z - primary.z) * scale;
//   this.ellipseCurve.update(
//     (this.rotatingReferenceFrame.x - primary.x - ellipse.focus) * scale,
//     (this.rotatingReferenceFrame.y - primary.y) * scale,
//     ellipse.xRadius * scale,
//     ellipse.yRadius * scale,
//     0,
//     2 * Math.PI,
//     false,
//     0,
//     { x: scenario.i, y: 0, z: scenario.w + scenario.e * 360 }
//   );
//   */
//   this.ellipseCurve.visible = true;
//   const scenario = this.scenario;
//   const primary = getObjFromArrByKeyValuePair(
//     scenario.masses,
//     'name',
//     scenario.primary
//   );

//   const earth = getObjFromArrByKeyValuePair(scenario.masses, 'name', 'Earth');
//   const mars = getObjFromArrByKeyValuePair(scenario.masses, 'name', 'Mars');
//   const tof = 0.5;
//   const M = 0;
//   const numiter = 35;
//   const rtol = 1e-15;
//   const k = primary.m * 39.5;

//   const vs = planFlight(tof, earth, mars, primary, 39.5, M, rtol, numiter);
//   const initVelocity = vs[0].initVel;

//   /*
//   const c = v2.set(vecMars).subtract(v1.set(vecEarth)).toObject();
//   const cNorm = v1.set(c).getLength();
//   const r1 = v1.set(vecEarth).getLength();
//   const r2 = v1.set(vecMars).getLength();
//   const s = 0.5 * (r1 + r2 + cNorm);

//   const ir1 = v1.set(vecEarth).normalise().toObject();
//   const ir2 = v1.set(vecMars).normalise().toObject();
//   let ih = v1.set(vecEarth).cross(v2.set(vecMars)).normalise().toObject();

//   let ll = Math.sqrt(1 - Math.min(1.0, cNorm / s));
//   if (ih.z < 0) {
//     ll = -ll;
//     ih = v1.set(ih).multiplyByScalar(-1).toObject();
//   }
//   const it1 = v1.set(ih).cross(v2.set(ir1)).toObject();
//   const it2 = v1.set(ih).cross(v2.set(ir2)).toObject();

//   const T = Math.sqrt(2 * k /(s ** 3)) * tof;

//   const gamma = Math.sqrt(k * s / 2);
//   const rho = (r1 - r2) / cNorm;
//   const sigma = Math.sqrt(1 - rho * rho);
//   const sol = findXY(ll, T, M, numiter, rtol);
//   const {x, y} = sol[0];
//   const {Vr1, Vr2, Vt1, Vt2} = reconstruct(x, y, r1, r2, ll, gamma, rho, sigma);
//   const initVelocity = v1.set(ir1).multiplyByScalar(Vr1).addScaledVector(Vt1, v2.set(it1)).toObject();
//   const finalVelocity = v1.set(ir2).multiplyByScalar(Vr2).addScaledVector(Vt2, v2.set(it2)).toObject();
//   console.log("Final: ", finalVelocity);
//   console.log("Initial: ", initVelocity);
//   */

//   const keplerElements = stateToKepler(earth, initVelocity, k);
//   //console.log("a: ", keplerElements.a)
//   //console.log("e: ", keplerElements.e)
//   //console.log("blandat: ", { x: keplerElements.argp, y: keplerElements.i, z: keplerElements.omega })
//   const scale = scenario.scale;
//   const ellipse = getEllipse(keplerElements.a, keplerElements.e);
//   this.ellipseCurve.position.z =
//     (this.rotatingReferenceFrame.z - primary.z) * scale;
//   console.log({ x: keplerElements.argp, y: keplerElements.i, z: keplerElements.omega })
//   console.log(keplerElements.a, keplerElements.e)
//   this.ellipseCurve.update(
//     (this.rotatingReferenceFrame.x - primary.x - ellipse.focus) * scale,
//     (this.rotatingReferenceFrame.y - primary.y) * scale,
//     ellipse.xRadius * scale,
//     ellipse.yRadius * scale,
//     0,
//     2 * Math.PI,
//     false,
//     0,
//     { x: scenario.i, y: scenario.w, z: keplerElements.argp + keplerElements.omega - 180 }
//   );

//   //if (v1.cross(v2).x === 0) {return}

//   /*
//   const r1 = Math.sqrt(
//     Math.pow(Math.abs(earth.x - primary.x), 2) +
//       Math.pow(Math.abs(earth.y - primary.y), 2) +
//       Math.pow(Math.abs(earth.z - primary.z), 2)
//   );
//   const r2 = Math.sqrt(
//     Math.pow(Math.abs(mars.x - primary.x), 2) +
//       Math.pow(Math.abs(mars.y - primary.y), 2) +
//       Math.pow(Math.abs(mars.z - primary.z), 2)
//   );
//   const rm = (r1 + r2) / 2;
//   const dotProduct = earth.x * mars.x + earth.y * mars.y + earth.z * mars.z;
//   const cosAlpha = dotProduct / (r1 * r2);
//   //const alpha = Math.acos(cosAlpha)
//   //const sinAlpha = Math.sin(alpha)
//   const d = Math.sqrt(r1 * r1 + r2 * r2 - 2 * r1 * r2 * cosAlpha) / 2;
//   const A = (r2 - r1) / 2;
//   const B = Math.sqrt(d * d - A * A);
//   const E = d / A;
//   const x0 = -rm / E;
//   const y0 = B * Math.sqrt(Math.pow(Math.abs(x0 / A), 2) - 1);
//   const y = 1;
//   const x = A * Math.sqrt(1 + Math.pow(Math.abs(y / B), 2));
//   const a = (rm + E * x) / 2;
//   const e =
//     Math.sqrt(Math.pow(Math.abs(x0 - x), 2) + Math.pow(Math.abs(y0 - y), 2)) /
//     (2 * a);

//   const normalVector = {
//     x: earth.y * mars.z - earth.z * mars.y,
//     y: earth.z * mars.x - earth.x * mars.z,
//     z: earth.x * mars.y - earth.y * mars.x
//   };
//   const normalLength = Math.sqrt(
//     normalVector.x * normalVector.x +
//       normalVector.y * normalVector.y +
//       normalVector.z * normalVector.z
//   );
//   const cosIncl = normalVector.z / normalLength;
//   const i = Math.acos(cosIncl) * 180 / Math.PI;
//   //let sinTrueAnomaly = ((x0 + d) - y0) / r1
//   //if (sinAlpha < 0) {
//   //  sinTrueAnomaly = -sinTrueAnomaly
//   //}

//   const ellipse = getEllipse(a, e);
//   */
// },
